import React from "react";
import Logo from "../../../assets/images/ededaf logo.png";
import { XMarkIcon } from "@heroicons/react/24/outline";
import IconButtonV2 from "../../../components/IconButtonV2";
import IconCourses from "../../../components/Icons/IconCourses";
import IconDashboard from "../../../components/Icons/IconDashboard";
import IconCalendar from "../../../components/Icons/IconCalendar";
import IconList from "../../../components/Icons/IconList";
import CustomButton from "../../../components/Button";
import IconAdministration from "../../../components/Icons/IconAdministration";

interface MobileSidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  adminOpen: boolean;
  setAdminOpen: (open: boolean) => void;
  role: string | undefined;
  setActiveComponent: (component: string) => void;
  setActiveAdminComponent: (component: string) => void;
  activeComponent: string;
  activeAdminComponent: string;
}

const MobileSidebar: React.FC<MobileSidebarProps> = ({
  sidebarOpen,
  setSidebarOpen,
  adminOpen,
  setAdminOpen,
  role,
  setActiveComponent,
  setActiveAdminComponent,
  activeComponent,
  activeAdminComponent,
}) => {
  const getDefaultAdminComponent = () => {
    if (role === "admin") {
      return "adminDashboard";
    }

    if (role === "facilitator") {
      return "adminFacilitatorDashboard";
    }

    return "adminUsers";
  };

  const hasAdministrationAccess =
    role === "admin" ||
    role === "technical staff" ||
    role === "staff manager" ||
    role === "facilitator";

  const canViewUsers = role === "admin" || role === "staff manager";
  const isAdministrationActive = activeComponent === "administration";

  return (
    <div className="lg:hidden">
      <div
        className={`fixed inset-0 z-50 flex bg-slate-950/20 transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="relative m-3 flex w-full max-w-xs flex-col overflow-hidden rounded-[28px] bg-[linear-gradient(180deg,#ffffff_0%,#f8fbfb_100%)] p-4 shadow-[0_24px_70px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/70">
          <div className="flex items-start justify-between gap-4">
            <div className="inline-flex items-center justify-center rounded-[24px] bg-white/90 px-2 py-1 shadow-sm ring-1 ring-slate-100">
              <img src={Logo} alt="Logo" className="h-[96px] w-auto" />
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="mt-1 rounded-xl bg-slate-100 p-2 text-slate-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <nav className="mt-5 flex flex-1 flex-col gap-y-2 overflow-y-auto pr-1">
            <IconButtonV2
              icon={<IconDashboard />}
              label="Dashboard"
              active={activeComponent === "dashboard"}
              onClick={() => {
                setActiveComponent("dashboard");
                setSidebarOpen(false);
              }}
            />
            {role !== "external user" && (
              <IconButtonV2
                icon={<IconCourses />}
                label="Courses"
                active={activeComponent === "courses"}
                onClick={() => {
                  setActiveComponent("courses");
                  setSidebarOpen(false);
                }}
              />
            )}
            {role !== "external user" && (
              <IconButtonV2
                icon={<IconList />}
                label="Newsletters"
                active={activeComponent === "newsletters"}
                onClick={() => {
                  setActiveComponent("newsletters");
                  setSidebarOpen(false);
                }}
              />
            )}
            {hasAdministrationAccess && (
              <IconButtonV2
                icon={<IconCalendar strokeWidth="1" />}
                label="My Bookings"
                active={activeComponent === "bookings"}
                onClick={() => {
                  setActiveComponent("bookings");
                  setSidebarOpen(false);
                }}
              />
            )}
            {hasAdministrationAccess && (
              <div className="flex flex-col gap-2">
                <IconButtonV2
                  icon={<IconAdministration />}
                  label="Administration"
                  active={isAdministrationActive}
                  onClick={() => {
                    setAdminOpen(!adminOpen);
                    setActiveComponent("administration");
                    setActiveAdminComponent(getDefaultAdminComponent());
                  }}
                />
                <div
                  className={`ml-4 px-1 overflow-hidden transition-all duration-300 ${
                    adminOpen
                      ? "max-h-[800px] opacity-100 overflow-y-auto pb-4"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="mt-1 flex flex-col rounded-[20px] bg-slate-50/90 p-3 ring-1 ring-slate-200/80">
                    <div className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Admin tools
                    </div>
                    <div className="flex flex-col gap-1 pr-1">
                      {role === "admin" && (
                        <>
                          <CustomButton
                            label="Dashboard"
                            padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                            active={
                              isAdministrationActive &&
                              activeAdminComponent === "adminDashboard"
                            }
                            onClick={() => {
                              setActiveComponent("administration");
                              setActiveAdminComponent("adminDashboard");
                              setSidebarOpen(false);
                            }}
                          />
                          <CustomButton
                            label="Users"
                            padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                            active={
                              isAdministrationActive &&
                              activeAdminComponent === "adminUsers"
                            }
                            onClick={() => {
                              setActiveComponent("administration");
                              setActiveAdminComponent("adminUsers");
                              setSidebarOpen(false);
                            }}
                          />
                          <CustomButton
                            label="Courses"
                            padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                            active={
                              isAdministrationActive &&
                              activeAdminComponent === "adminCourses"
                            }
                            onClick={() => {
                              setActiveComponent("administration");
                              setActiveAdminComponent("adminCourses");
                              setSidebarOpen(false);
                            }}
                          />
                          <CustomButton
                            label="Newsletters"
                            padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                            active={
                              isAdministrationActive &&
                              activeAdminComponent === "adminNewsletters"
                            }
                            onClick={() => {
                              setActiveComponent("administration");
                              setActiveAdminComponent("adminNewsletters");
                              setSidebarOpen(false);
                            }}
                          />
                          <CustomButton
                            label="Classes"
                            padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                            active={
                              isAdministrationActive &&
                              activeAdminComponent === "adminClasses"
                            }
                            onClick={() => {
                              setActiveComponent("administration");
                              setActiveAdminComponent("adminClasses");
                              setSidebarOpen(false);
                            }}
                          />
                          <CustomButton
                            label="Bookings"
                            padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                            active={
                              isAdministrationActive &&
                              activeAdminComponent === "adminBookings"
                            }
                            onClick={() => {
                              setActiveComponent("administration");
                              setActiveAdminComponent("adminBookings");
                              setSidebarOpen(false);
                            }}
                          />
                          <CustomButton
                            label="Resources"
                            padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                            active={
                              isAdministrationActive &&
                              activeAdminComponent === "adminResources"
                            }
                            onClick={() => {
                              setActiveComponent("administration");
                              setActiveAdminComponent("adminResources");
                              setSidebarOpen(false);
                            }}
                          />
                          <CustomButton
                            label="Compliance Policy"
                            padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                            active={
                              isAdministrationActive &&
                              activeAdminComponent === "adminCompliancePolicy"
                            }
                            onClick={() => {
                              setActiveComponent("administration");
                              setActiveAdminComponent("adminCompliancePolicy");
                              setSidebarOpen(false);
                            }}
                          />
                        </>
                      )}
                      {role === "facilitator" && (
                        <CustomButton
                          label="Dashboard"
                          padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                          active={
                            isAdministrationActive &&
                            activeAdminComponent === "adminFacilitatorDashboard"
                          }
                          onClick={() => {
                            setActiveComponent("administration");
                            setActiveAdminComponent(
                              "adminFacilitatorDashboard",
                            );
                            setSidebarOpen(false);
                          }}
                        />
                      )}
                      {canViewUsers && role !== "admin" && (
                        <CustomButton
                          label="Users"
                          padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                          active={
                            isAdministrationActive &&
                            activeAdminComponent === "adminUsers"
                          }
                          onClick={() => {
                            setActiveComponent("administration");
                            setActiveAdminComponent("adminUsers");
                            setSidebarOpen(false);
                          }}
                        />
                      )}
                      {(role === "technical staff" || role === "admin") && (
                        <CustomButton
                          label="Staff Equipment"
                          padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                          active={
                            isAdministrationActive &&
                            activeAdminComponent === "adminStaffEquipment"
                          }
                          onClick={() => {
                            setActiveComponent("administration");
                            setActiveAdminComponent("adminStaffEquipment");
                            setSidebarOpen(false);
                          }}
                        />
                      )}
                      {(role === "staff manager" || role === "admin") && (
                        <CustomButton
                          label="KPI"
                          padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                          active={
                            isAdministrationActive &&
                            activeAdminComponent === "adminKPIs"
                          }
                          onClick={() => {
                            setActiveComponent("administration");
                            setActiveAdminComponent("adminKPIs");
                            setSidebarOpen(false);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </nav>
        </div>
        <div className="flex-grow" onClick={() => setSidebarOpen(false)}></div>
      </div>
    </div>
  );
};

export default MobileSidebar;
